import React, { useEffect } from 'react'
import { BrowserRouter, Switch, Route, Redirect } from 'react-router-dom'
import Root from './app/root'
import { createBrowserHistory } from 'history'
import { useSelector } from 'react-redux'
import { MuiThemeProvider } from '@material-ui/core/styles'
import { UserManagement, User, Role } from './app/user-management'
import { Customers, Customer } from './app/customers'
import { Dashboard } from './app/dashboard'
import Locations from './app/locations'
import { Devices, Device } from './app/devices'
import { NetworkDevice } from './app/network-devices'
import Sharing from './app/sharing'
import Profile from './app/profile'
import Faq from './app/faq'
import Login from './app/login'
import TwoFactor from './app/login/two-factor'
import { SnackbarCustomProvider } from './app/root/components/common'
import PrivateRoute from './app/root/components/private-route'
import Notifier from './app/root/components/notifier'
import Init from './app/root/init'
import Register from './app/register'
import axios from 'axios'
import { assignInterceptors, initDelightedSurvey } from './utils'
import './app/root/setup-auth-interceptor'
import ErrorPage from './app/root/components/error-page'
import TermsAndServices from './app/terms-and-services'
import { Reports, Report } from './app/reports'
import { ForgotPassword, ResetPassword } from './app/reset-password'
import Integrations from './app/integrations'
import { UserPermissions } from './services/permissions'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import MomentUtils from '@date-io/moment'
import moment from 'moment'
import 'moment/min/locales'
import { HotSwap } from './app/hot-swap'
import HotSwapStart from './app/hot-swap/hot-swap-start'
import HotSwapAdmin from './app/hot-swap/hot-swap-admin'
import HotSwapDetails from './app/hot-swap/hot-swap-details'
import {
    SwapReplacements,
    SwapReplacementsArchive,
    SwapReplacementsMain
} from './app/swap-replacements'
import SimCards from './app/sim-cards/sim-cards'
import Billing from './app/billing'
import Monetisation from './app/monetisation'

const history = createBrowserHistory()

export default function TraplinkedBase() {
    const { theme, token, user, p, localeCode } = useSelector(
        state => state.main
    )

    if (token && !UserPermissions.isSet) {
        UserPermissions.init(user, p)
    }
    if (!token) {
        UserPermissions.flush()
    }

    assignInterceptors(axios, token)
    initDelightedSurvey()

    useEffect(() => {
        moment.locale(localeCode)
    }, [localeCode])

    return (
        <MuiPickersUtilsProvider
            utils={MomentUtils}
            locale={localeCode}
            libInstance={moment}>
            <MuiThemeProvider theme={theme}>
                <BrowserRouter history={history}>
                    <SnackbarCustomProvider>
                        <Notifier />

                        <Root>
                            <Switch>
                                <Route exact path="/" render={() => <Init />} />
                                <Route
                                    exact
                                    path="/login"
                                    render={() => <Login />}
                                />
                                <Route
                                    exact
                                    path="/forgot-password"
                                    render={() => <ForgotPassword />}
                                />
                                <Route
                                    exact
                                    path="/reset-password"
                                    render={() => <ResetPassword />}
                                />
                                <Route
                                    exact
                                    path="/register"
                                    render={() => <Register />}
                                />
                                <Route
                                    exact
                                    path="/ts"
                                    render={() => <TermsAndServices />}
                                />
                                <Route
                                    exact
                                    path="/verify-2fa"
                                    render={() => <TwoFactor />}
                                />
                                <PrivateRoute
                                    exact
                                    path="/dashboard"
                                    render={() => <Dashboard />}
                                />
                                <PrivateRoute
                                    exact
                                    path="/user-management/users/:id"
                                    render={() => <User />}
                                />
                                <PrivateRoute
                                    exact
                                    path="/user-management/roles/:roleId"
                                    render={() => <Role />}
                                />
                                <PrivateRoute
                                    path="/user-management/:tabName?"
                                    render={() => <UserManagement />}
                                />
                                <PrivateRoute
                                    exact
                                    path="/customers"
                                    render={() => <Customers />}
                                />
                                <PrivateRoute
                                    path="/locations/:id?"
                                    render={() => <Locations />}
                                />
                                <PrivateRoute
                                    exact
                                    path="/customers/:id"
                                    render={() => <Customer />}
                                />
                                <PrivateRoute
                                    exact
                                    path="/devices"
                                    render={() => <Devices />}
                                />
                                <PrivateRoute
                                    path={[
                                        '/devices/:deviceId/:tab',
                                        '/devices/:deviceId'
                                    ]}
                                    render={() => <Device />}
                                />
                                <PrivateRoute
                                    path={[
                                        '/network-devices/:networkDeviceId/'
                                    ]}
                                    render={() => <NetworkDevice />}
                                />
                                <PrivateRoute
                                    exact
                                    path="/profile"
                                    render={() => <Profile />}
                                />
                                <PrivateRoute
                                    exact
                                    path="/faq"
                                    render={() => <Faq />}
                                />
                                <PrivateRoute
                                    exact
                                    path="/reports"
                                    render={() => <Reports />}
                                />
                                <PrivateRoute
                                    exact
                                    path="/reports/:id"
                                    render={() => <Report />}
                                />
                                <PrivateRoute
                                    exact
                                    path="/sharing"
                                    render={() => <Sharing />}
                                />
                                <Route
                                    exact
                                    path="/api-keys"
                                    render={() => (
                                        <Redirect to="/integrations/api-keys" />
                                    )}
                                />
                                <PrivateRoute
                                    path="/integrations/:tabName?"
                                    render={() => <Integrations />}
                                />
                                <PrivateRoute
                                    path="/swap-replacements/:tabName?"
                                    render={() => <SwapReplacements />}
                                />
                                <PrivateRoute
                                    exact
                                    path="/hot-swap"
                                    render={() => <HotSwap />}
                                />
                                <PrivateRoute
                                    exact
                                    path="/hot-swap/start"
                                    render={() => <HotSwapStart />}
                                />
                                <PrivateRoute
                                    exact
                                    path="/hot-swap/:hotswapId"
                                    render={() => <HotSwapDetails />}
                                />
                                <PrivateRoute
                                    exact
                                    path="/hot-swap-admin"
                                    render={() => <HotSwapAdmin />}
                                />
                                <PrivateRoute
                                    exact
                                    path="/sim-cards"
                                    render={() => <SimCards />}
                                />
                                <PrivateRoute
                                    exact
                                    path="/billing"
                                    render={() => <Billing />}
                                />
                                <PrivateRoute
                                    exact
                                    path="/monetisation"
                                    render={() => <Monetisation />}
                                />

                                <PrivateRoute
                                    exact
                                    path="*"
                                    render={() => <ErrorPage />}
                                />
                            </Switch>
                        </Root>
                    </SnackbarCustomProvider>
                </BrowserRouter>
            </MuiThemeProvider>
        </MuiPickersUtilsProvider>
    )
}
